import { useState, type MouseEvent } from 'react';
import { ArrowRight, RotateCcw, Check, AlertTriangle, AlertOctagon } from 'lucide-react';
import { BotanicalAccent } from '@/components/ui/botanical-accent';
import { CtaArrow } from '@/components/ui/cta-arrow';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { SplitText } from '@/components/ui/split-text';
import { MagneticButton } from '@/components/ui/magnetic-button';
import { useCalendly } from '@/lib/CalendlyContext';
import { useLang } from '@/lib/LanguageContext';
import { cn } from '@/lib/utils';

type Lang = 'fr' | 'en';
type Outcome = 'solide' | 'a-renforcer' | 'critique';

interface Question {
  q: { fr: string; en: string };
  options: { fr: string; en: string; weight: number }[];
}

const QUESTIONS: Question[] = [
  {
    q: {
      fr: 'Avez-vous une assurance collective via votre employeur ?',
      en: 'Do you have group insurance through your employer?'
    },
    options: [
      { fr: 'Oui, complète', en: 'Yes, comprehensive', weight: 0 },
      { fr: 'Oui, mais basique', en: 'Yes, but basic', weight: 1 },
      { fr: 'Non', en: 'No', weight: 2 },
      { fr: 'Je ne sais pas', en: 'I don\'t know', weight: 2 }
    ]
  },
  {
    q: {
      fr: 'Avez-vous des personnes à charge (enfants, conjoint, parents) ?',
      en: 'Do you have dependents (children, partner, parents)?'
    },
    options: [
      { fr: 'Oui, plusieurs', en: 'Yes, several', weight: 2 },
      { fr: 'Oui, un ou deux', en: 'Yes, one or two', weight: 1 },
      { fr: 'Non', en: 'No', weight: 0 }
    ]
  },
  {
    q: {
      fr: 'Avez-vous une hypothèque ou des dettes de plus de 50 000 $ ?',
      en: 'Do you have a mortgage or debts over $50,000?'
    },
    options: [
      { fr: 'Oui', en: 'Yes', weight: 2 },
      { fr: 'Non', en: 'No', weight: 0 }
    ]
  },
  {
    q: {
      fr: 'Connaissez-vous quelqu\'un dans votre entourage qui a vécu un diagnostic grave (cancer, AVC, infarctus) ?',
      en: 'Do you know someone close who experienced a critical diagnosis (cancer, stroke, heart attack)?'
    },
    options: [
      { fr: 'Oui, et j\'ai vu l\'impact financier réel', en: 'Yes, and I saw the real financial impact', weight: 2 },
      { fr: 'Oui, mais sans impact financier majeur', en: 'Yes, but no major financial impact', weight: 1 },
      { fr: 'Non, pas directement', en: 'No, not directly', weight: 1 }
    ]
  },
  {
    q: {
      fr: 'Si un diagnostic grave tombait demain, votre situation financière tiendrait combien de mois sans votre salaire ?',
      en: 'If a critical diagnosis hit tomorrow, how many months could your finances last without your salary?'
    },
    options: [
      { fr: 'Plus de 12 mois', en: 'More than 12 months', weight: 0 },
      { fr: 'Entre 3 et 12 mois', en: 'Between 3 and 12 months', weight: 1 },
      { fr: 'Moins de 3 mois', en: 'Less than 3 months', weight: 2 },
      { fr: 'Je n\'ai jamais fait le calcul', en: 'I\'ve never done the math', weight: 2 }
    ]
  }
];

function computeOutcome(answers: number[]): Outcome {
  const total = answers.reduce((sum, idx, qIdx) => {
    const weight = QUESTIONS[qIdx].options[idx]?.weight ?? 0;
    return sum + weight;
  }, 0);
  if (total <= 3) return 'solide';
  if (total <= 6) return 'a-renforcer';
  return 'critique';
}

const OUTCOMES: Record<
  Outcome,
  {
    icon: typeof Check;
    titleColor: string;
    title: { fr: string; en: string };
    body: { fr: string; en: string };
    cta: { fr: string; en: string };
  }
> = {
  solide: {
    icon: Check,
    titleColor: 'text-secondary',
    title: {
      fr: 'Votre couverture semble solide.',
      en: 'Your coverage looks solid.'
    },
    body: {
      fr: 'Vous êtes dans une bonne posture. Un audit gratuit reste pertinent pour vérifier que votre collectif couvre bien le cancer comme diagnostic (et pas seulement l\'invalidité). 45 minutes, sans engagement.',
      en: 'You\'re in a good position. A free audit is still relevant to verify your group plan actually covers cancer as a diagnosis (not just disability). 45 minutes, no commitment.'
    },
    cta: {
      fr: 'Vérifier mon collectif',
      en: 'Verify my group plan'
    }
  },
  'a-renforcer': {
    icon: AlertTriangle,
    titleColor: 'text-tertiary',
    title: {
      fr: 'Votre couverture comporte des zones à renforcer.',
      en: 'Your coverage has zones to strengthen.'
    },
    body: {
      fr: 'Vous avez probablement une partie de la protection nécessaire, mais des écarts existent. La plupart de mes clients m\'arrivent avec ce profil. On regarde ensemble ce qui manque, sans engagement.',
      en: 'You probably have part of the protection needed, but gaps exist. Most of my clients arrive with this profile. We look together at what\'s missing, no commitment.'
    },
    cta: {
      fr: 'Identifier mes écarts',
      en: 'Identify my gaps'
    }
  },
  critique: {
    icon: AlertOctagon,
    titleColor: 'text-primary',
    title: {
      fr: 'Votre couverture présente des écarts critiques.',
      en: 'Your coverage has critical gaps.'
    },
    body: {
      fr: 'Vos réponses indiquent une exposition financière importante en cas de diagnostic grave. Pas un constat pour vous faire peur — un constat pour qu\'on regarde ensemble, vite, ce qui peut être protégé. 45 minutes, gratuit.',
      en: 'Your answers indicate significant financial exposure if a critical diagnosis occurs. Not a scare tactic — a finding to address together, soon. 45 minutes, free.'
    },
    cta: {
      fr: 'Demander mon audit',
      en: 'Request my audit'
    }
  }
};

const UI = {
  eyebrow: { fr: 'Diagnostic 5 questions', en: '5-question diagnostic' },
  title: {
    fr: 'Où en êtes-vous vraiment ?',
    en: 'Where do you actually stand?'
  },
  subtitle: {
    fr: '5 questions, 90 secondes. Résultat instantané, sans courriel demandé.',
    en: '5 questions, 90 seconds. Instant result, no email required.'
  },
  start: { fr: 'Commencer', en: 'Start' },
  progress: { fr: 'Question', en: 'Question' },
  of: { fr: 'sur', en: 'of' },
  retake: { fr: 'Refaire le diagnostic', en: 'Retake' }
};

function getText<T extends { fr: string; en: string }>(obj: T, lang: Lang): string {
  return obj[lang];
}

export function DiagnosticQuiz5Q() {
  const { lang } = useLang();
  const { open: openCalendly } = useCalendly();
  const [started, setStarted] = useState(false);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleCalendlyClick = (e: MouseEvent) => {
    e.preventDefault();
    openCalendly();
  };

  const handleAnswer = (optIdx: number) => {
    const newAnswers = [...answers, optIdx];
    setAnswers(newAnswers);
    if (qIdx === QUESTIONS.length - 1) {
      setShowResult(true);
    } else {
      setQIdx(qIdx + 1);
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setQIdx(0);
    setAnswers([]);
    setShowResult(false);
  };

  const outcome = showResult ? computeOutcome(answers) : null;
  const outcomeData = outcome ? OUTCOMES[outcome] : null;
  const OutcomeIcon = outcomeData?.icon;

  // Phase 27.31 — fix scroll jump : DiagnosticQuiz height réel ~779px
  // (mesuré Playwright 1280×820), placeholder section-cv-auto (1200px)
  // générait -421px de delta off-screen. section-cv-short (600px) plus
  // proche (799 vs 600 = +179px tolérable).
  return (
    <section id="diagnostic" className="section-cv-short atmosphere-watercolor surface-cream-deep relative w-full overflow-hidden py-20 md:py-32">
      {/* Phase 27.17 — Combo E : morning light rose-blush opening at top */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-20 bg-gradient-to-b from-rose-blush/40 to-transparent md:h-24"
      />
      <BotanicalAccent variant="sprig-peach" position="bottom-right" size="md" opacity={0.14} />
      {/* Phase 27.17 — Combo G : rose-tendril 5th variant accent at top-left */}
      <BotanicalAccent variant="rose-tendril" position="top-left" size="sm" opacity={0.16} />
      <div className="relative z-[1] mx-auto max-w-3xl px-5 md:px-8">
        {/* Header */}
        <div className="mb-12 text-center md:mb-16">
          <ScrollReveal as="span" className="eyebrow-pill mb-6">
            {getText(UI.eyebrow, lang)}
          </ScrollReveal>
          <SplitText as="h2" className="mb-6 font-display text-4xl leading-[1.15] text-balance text-foreground md:text-5xl">
            {getText(UI.title, lang)}
          </SplitText>
          <ScrollReveal delay={200} as="p" className="mx-auto max-w-xl font-sans text-base text-pretty text-muted-foreground">
            {getText(UI.subtitle, lang)}
          </ScrollReveal>
        </div>

        {/* Quiz card */}
        <ScrollReveal delay={300}>
          <div className={cn(
            'card-warm relative rounded-2xl bg-background p-8 shadow-warm-md md:p-12',
            showResult && 'border-beam transition-colors duration-300 hover:bg-rose-blush/12'
          )}>
            {/* === Intro state === */}
            {!started && !showResult && (
              <div className="flex flex-col items-center gap-8 py-8 text-center">
                <p className="max-w-md font-display text-xl italic leading-[1.4] text-pretty text-foreground">
                  {lang === 'fr'
                    ? 'Vos réponses restent privées. Aucune adresse courriel demandée, aucun suivi.'
                    : 'Your answers stay private. No email asked, no follow-up.'}
                </p>
                <MagneticButton>
                  <button
                    type="button"
                    onClick={() => setStarted(true)}
                    data-cursor="cta"
                    className="group border-beam inline-flex items-center gap-4 rounded-full bg-primary py-3 pl-7 pr-2 font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
                  >
                    <span>{getText(UI.start, lang)}</span>
                    <CtaArrow tone="inverted" />
                  </button>
                </MagneticButton>
              </div>
            )}

            {/* === Question state === */}
            {started && !showResult && (
              <div className="flex flex-col gap-8">
                <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  <span>
                    {getText(UI.progress, lang)} {qIdx + 1} {getText(UI.of, lang)} {QUESTIONS.length}
                  </span>
                  <div className="flex gap-1.5">
                    {QUESTIONS.map((_, i) => (
                      <span
                        key={i}
                        aria-hidden="true"
                        className={cn(
                          'h-1.5 w-6 rounded-full transition-colors',
                          i < qIdx
                            ? 'bg-primary'
                            : i === qIdx
                              ? 'bg-primary/60'
                              : 'bg-border'
                        )}
                      />
                    ))}
                  </div>
                </div>

                <h3 className="font-display text-2xl leading-[1.25] text-balance text-foreground md:text-3xl">
                  {getText(QUESTIONS[qIdx].q, lang)}
                </h3>

                <div className="flex flex-col gap-3">
                  {QUESTIONS[qIdx].options.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleAnswer(i)}
                      className="group flex items-center justify-between rounded-md border border-border bg-background px-5 py-4 text-left font-sans text-base text-foreground transition-all hover:border-primary hover:bg-primary/5"
                    >
                      <span>{getText(opt, lang)}</span>
                      <ArrowRight
                        className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-primary"
                        strokeWidth={1.7}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* === Result state === */}
            {showResult && outcomeData && OutcomeIcon && (
              <div className="flex flex-col gap-8">
                <div className="flex flex-col items-start gap-4">
                  <OutcomeIcon
                    className={cn('h-10 w-10', outcomeData.titleColor)}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <h3 className={cn('font-display text-2xl leading-[1.2] text-balance md:text-3xl', outcomeData.titleColor)}>
                    {getText(outcomeData.title, lang)}
                  </h3>
                </div>

                <p className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-foreground/85">
                  {getText(outcomeData.body, lang)}
                </p>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <MagneticButton>
                    <button
                      type="button"
                      onClick={handleCalendlyClick}
                      data-cursor="cta"
                      className="group border-beam inline-flex items-center gap-4 rounded-full bg-primary py-3 pl-7 pr-2 font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
                    >
                      <span>{getText(outcomeData.cta, lang)}</span>
                      <CtaArrow tone="inverted" />
                    </button>
                  </MagneticButton>
                  <button
                    type="button"
                    onClick={handleRestart}
                    className="inline-flex items-center gap-2 font-sans text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.7} aria-hidden="true" />
                    {getText(UI.retake, lang)}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
